/**
 * 우측 상세 패널: 선택 노드 상세 / 선택 없으면 전체 파이프라인 요약.
 */
import { AlertOctagon, ArrowDownToLine, ArrowUpFromLine, Clock, Gauge, Percent, RotateCw, Waves, X } from "lucide-react";
import { memo, useMemo } from "react";
import { MetricCard } from "../metrics/MetricCard";
import { ThroughputChart } from "../metrics/ThroughputChart";
import { FACTORIES } from "../../data/machines";
import { STATUS_LABEL_KO, formatInt, formatLatency, formatLocalDateTime, formatLocalTime, formatNumber, formatPercent, formatThroughput, formatTimeTooltip, statusColorVar, } from "../../lib/format";
import { selectActions, selectEdges, selectEvents, selectMode, selectNodeHistory, selectNodes, selectSelectedNode, selectSelectedNodeId, selectSummary, usePipelineStore, } from "../../store/pipeline-store";
import { IconButton, LevelBadge, NodeTypeIcon, SectionTitle, Skeleton, StatusBadge, StatusDot, cn, nodeTypeLabel } from "./ui";
const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
function extraValue(v) {
    if (v == null || v === "")
        return { text: "—" };
    if (typeof v === "number")
        return { text: Number.isInteger(v) ? formatInt(v) : formatNumber(v) };
    if (ISO_RE.test(v))
        return { text: formatLocalDateTime(v), title: formatTimeTooltip(v) };
    return { text: v, title: v };
}
function ConnectionList({ title, icon: Icon, items, onPick, }) {
    return (<div className="min-w-0">
      <div className="flex items-center gap-1 px-1 text-[11px] text-fg-subtle">
        <Icon aria-hidden size={12}/>
        {title} <span className="tabular">({items.length})</span>
      </div>
      {items.length === 0 ? (<div className="px-1 py-1 text-[11px] text-fg-subtle">없음</div>) : (<ul className="thin-scroll mt-1 max-h-40 space-y-px overflow-y-auto">
          {items.map((n) => (<li key={n.id}>
              <button type="button" onClick={() => onPick(n.id)} title={`${n.data.label} 선택`} className="flex w-full min-w-0 items-center gap-1.5 rounded px-1.5 py-1 text-left text-xs text-fg-muted hover:bg-surface-hover hover:text-fg">
                <StatusDot status={n.data.status}/>
                <NodeTypeIcon nodeType={n.data.nodeType} size={12}/>
                <span className="min-w-0 flex-1 truncate">{n.data.label}</span>
                <span className="tabular shrink-0 text-[10px] text-fg-subtle">
                  {formatThroughput(n.data.throughput, n.data.nodeType)}
                </span>
              </button>
            </li>))}
        </ul>)}
    </div>);
}
function EventRow({ e }) {
    return (<li className="flex min-w-0 items-start gap-2 px-1 py-1 text-[11px]">
      <span className="tabular shrink-0 text-fg-subtle" title={formatTimeTooltip(e.timestamp)}>
        {formatLocalTime(e.timestamp)}
      </span>
      <LevelBadge level={e.level}/>
      <span className="min-w-0 flex-1 truncate text-fg-muted" title={e.message}>
        {e.message}
      </span>
    </li>);
}
function successTone(v) {
    if (v >= 99)
        return "good";
    if (v >= 90)
        return "warn";
    return "bad";
}
function NodeDetail({ node, onClose }) {
    const d = node.data;
    const nodes = usePipelineStore(selectNodes);
    const edges = usePipelineStore(selectEdges);
    const events = usePipelineStore(selectEvents);
    const history = usePipelineStore(useMemo(() => selectNodeHistory(node.id), [node.id]));
    const { requestFocusNode } = usePipelineStore(selectActions);
    const { inputs, outputs } = useMemo(() => {
        const byId = new Map(nodes.map((n) => [n.id, n]));
        const inputs = [];
        const outputs = [];
        for (const e of edges) {
            if (e.target === node.id) {
                const s = byId.get(e.source);
                if (s)
                    inputs.push(s);
            }
            else if (e.source === node.id) {
                const t = byId.get(e.target);
                if (t)
                    outputs.push(t);
            }
        }
        return { inputs, outputs };
    }, [nodes, edges, node.id]);
    const nodeEvents = useMemo(() => events.filter((e) => e.nodeId === node.id).slice(0, 20), [events, node.id]);
    const lastError = useMemo(() => events.find((e) => e.nodeId === node.id && e.level === "error"), [events, node.id]);
    const extraEntries = useMemo(() => Object.entries(d.extra ?? {}), [d.extra]);
    const latencyTone = d.latency < 0 ? "muted" : d.status === "error" ? "bad" : d.status === "warning" ? "warn" : "default";
    return (<div className="flex h-full min-h-0 flex-col">
      <div className="flex items-start gap-2 border-b border-border p-3">
        <span aria-hidden className="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-lg border border-border bg-bg" style={{ borderColor: `color-mix(in srgb, ${statusColorVar(d.status)} 50%, transparent)` }}>
          <NodeTypeIcon nodeType={d.nodeType} size={16}/>
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-sm font-semibold" title={d.label}>
            {d.label}
          </h2>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-fg-muted">
            <span className="rounded border border-border px-1 py-px">{nodeTypeLabel(d.nodeType)}</span>
            {d.factoryId && (<span className="rounded border border-border px-1 py-px">
                {FACTORIES.find((f) => f.id === d.factoryId)?.name ?? d.factoryId}
              </span>)}
            <StatusBadge status={d.status}/>
          </div>
          <div className="mt-1 truncate font-mono text-[10px] text-fg-subtle" title={node.id}>
            {node.id}
          </div>
        </div>
        <IconButton label="상세 닫기 (Esc)" onClick={onClose} className="size-7">
          <X size={14}/>
        </IconButton>
      </div>

      <div className="thin-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
        <p className="px-3 pt-3 text-xs leading-relaxed text-fg-muted" title={d.description}>
          {d.description || "설명 없음"}
        </p>

        <div className="grid grid-cols-2 gap-2 px-3 pt-3">
          <MetricCard label="처리량" icon={Waves} value={formatNumber(d.throughput, d.throughput >= 100 ? 0 : 1)} unit={d.nodeType === "source" ? "shots/min" : "rows/min"} hint={d.nodeType === "source" ? "최근 10분 샷 수 기준 평균" : "분당 처리 행 수"} tone={d.status === "offline" ? "muted" : "default"}/>
          <MetricCard label="지연" icon={Gauge} value={formatLatency(d.latency)} hint={d.nodeType === "source"
            ? "마지막 샷 수신(recv_ts) 이후 경과"
            : d.nodeType === "processor"
                ? "heartbeat 나이 (초)"
                : "지연 (초)"} tone={latencyTone}/>
          <MetricCard label="성공률" icon={Percent} value={formatPercent(d.successRate)} hint={d.nodeType === "source"
            ? "최근 10분 quality=good 비율"
            : node.id.startsWith("collector:")
                ? "connected_machines / 설비 수"
                : node.id.startsWith("uploader:")
                    ? "uploaded / (uploaded + fail)"
                    : "성공률"} tone={successTone(d.successRate)}/>
          <MetricCard label="재시도" icon={RotateCw} value={formatInt(d.retryCount)} unit="회" hint={d.nodeType === "source" ? "최근 24h 결번 건수" : node.id.startsWith("uploader:") ? "upload_fail_1m" : "재시도 횟수"} tone={d.retryCount > 0 ? "warn" : "default"}/>
        </div>

        {lastError && (<div className="mx-3 mt-3 flex items-start gap-2 rounded-md border border-status-error/40 bg-status-error/10 p-2 text-[11px]">
            <AlertOctagon aria-hidden size={14} className="mt-px shrink-0 text-status-error"/>
            <div className="min-w-0">
              <div className="font-semibold text-status-error">최근 오류</div>
              <div className="truncate text-fg" title={lastError.message}>
                {lastError.message}
              </div>
              <div className="tabular text-fg-subtle" title={formatTimeTooltip(lastError.timestamp)}>
                {formatLocalDateTime(lastError.timestamp)}
              </div>
            </div>
          </div>)}

        <div className="mx-3 mt-3 flex items-center gap-2 text-[11px] text-fg-muted">
          <Clock aria-hidden size={12} className="shrink-0 text-fg-subtle"/>
          <span className="shrink-0">마지막 실행</span>
          <span className="tabular truncate text-fg" title={formatTimeTooltip(d.lastUpdated)}>
            {formatLocalDateTime(d.lastUpdated)}
          </span>
        </div>

        <SectionTitle>처리량 추이 (최근 60점)</SectionTitle>
        <div className="px-3">
          <ThroughputChart history={history} nodeType={d.nodeType} status={d.status} height={120}/>
        </div>

        <SectionTitle>연결</SectionTitle>
        <div className="grid grid-cols-1 gap-3 px-3 sm:grid-cols-2">
          <ConnectionList title="입력" icon={ArrowDownToLine} items={inputs} onPick={requestFocusNode}/>
          <ConnectionList title="출력" icon={ArrowUpFromLine} items={outputs} onPick={requestFocusNode}/>
        </div>

        {extraEntries.length > 0 && (<>
            <SectionTitle>부가 정보</SectionTitle>
            <div className="mx-3 overflow-hidden rounded-md border border-border">
              <table className="w-full table-fixed text-[11px]">
                <tbody>
                  {extraEntries.map(([k, v]) => {
                const { text, title } = extraValue(v);
                return (<tr key={k} className="border-b border-border last:border-b-0">
                        <th scope="row" className="w-[42%] truncate bg-bg px-2 py-1 text-left font-medium text-fg-subtle" title={k}>
                          {k}
                        </th>
                        <td className="tabular truncate px-2 py-1 text-fg" title={title ?? text}>
                          {text}
                        </td>
                      </tr>);
            })}
                </tbody>
              </table>
            </div>
          </>)}

        <SectionTitle right={<span className="tabular font-normal normal-case">{nodeEvents.length}건</span>}>
          이벤트 (최근 20건)
        </SectionTitle>
        <div className="px-2 pb-3">
          {nodeEvents.length === 0 ? (<div className="px-1 text-[11px] text-fg-subtle">이 노드의 이벤트가 없습니다.</div>) : (<ul className="divide-y divide-border/60">
              {nodeEvents.map((e) => (<EventRow key={e.id} e={e}/>))}
            </ul>)}
        </div>
      </div>
    </div>);
}
const STATUS_ORDER = ["running", "healthy", "warning", "recovering", "error", "offline"];
function PipelineSummaryView() {
    const nodes = usePipelineStore(selectNodes);
    const summary = usePipelineStore(selectSummary);
    const mode = usePipelineStore(selectMode);
    const { requestFocusNode } = usePipelineStore(selectActions);
    const byStatus = useMemo(() => {
        const m = { healthy: 0, running: 0, warning: 0, error: 0, recovering: 0, offline: 0 };
        for (const n of nodes)
            m[n.data.status]++;
        return m;
    }, [nodes]);
    const factories = useMemo(() => FACTORIES.map((f) => {
        const machines = nodes.filter((n) => n.data.nodeType === "source" && n.data.factoryId === f.id);
        const collector = nodes.find((n) => n.id === `collector:${f.id}`);
        const outbox = nodes.find((n) => n.id === `outbox:${f.id}`);
        const uploader = nodes.find((n) => n.id === `uploader:${f.id}`);
        const running = machines.filter((n) => n.data.status === "running" || n.data.status === "healthy").length;
        const problem = machines.filter((n) => n.data.status === "error" || n.data.status === "warning").length;
        const throughput = machines.reduce((a, n) => a + n.data.throughput, 0);
        return { f, machines, collector, outbox, uploader, running, problem, throughput };
    }), [nodes]);
    const topProblems = useMemo(() => nodes
        .filter((n) => n.data.status === "error" || n.data.status === "warning")
        .sort((a, b) => (a.data.status === b.data.status ? b.data.retryCount - a.data.retryCount : a.data.status === "error" ? -1 : 1))
        .slice(0, 8), [nodes]);
    if (mode === "idle" && nodes.length === 0) {
        return (<div className="space-y-3 p-3" aria-busy="true" aria-label="요약 불러오는 중">
        <Skeleton className="h-5 w-1/2"/>
        <div className="grid grid-cols-2 gap-2">
          {Array.from({ length: 4 }, (_, i) => (<Skeleton key={i} className="h-14"/>))}
        </div>
        <Skeleton className="h-24"/>
        <Skeleton className="h-24"/>
      </div>);
    }
    return (<div className="flex h-full min-h-0 flex-col">
      <div className="border-b border-border p-3">
        <h2 className="text-sm font-semibold">파이프라인 요약</h2>
        <p className="mt-0.5 text-[11px] text-fg-subtle">노드를 클릭하면 상세 정보가 표시됩니다.</p>
      </div>
      <div className="thin-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden pb-3">
        <div className="grid grid-cols-2 gap-2 px-3 pt-3">
          <MetricCard label="총 처리량" icon={Waves} value={formatNumber(summary.totalThroughput, summary.totalThroughput >= 100 ? 0 : 1)} unit="shots/min" hint="전체 설비 처리량 합계"/>
          <MetricCard label="평균 지연" icon={Gauge} value={formatLatency(summary.avgLatency)} hint="수집기 heartbeat 나이 평균"/>
          <MetricCard label="오류율" icon={Percent} value={formatPercent(summary.errorRate)} hint="오류 상태 노드 비율" tone={summary.errorRate > 0 ? "bad" : "good"}/>
          <MetricCard label="노드 수" value={formatInt(nodes.length)} unit="개" hint="설비 56 + 처리기 6 + 저장소 7 + 출력 2"/>
        </div>

        <SectionTitle>상태별 노드 수</SectionTitle>
        <ul className="mx-3 grid grid-cols-3 gap-1.5">
          {STATUS_ORDER.map((s) => (<li key={s} className="flex items-center gap-1.5 rounded-md border border-border bg-bg px-2 py-1 text-[11px]" title={STATUS_LABEL_KO[s]}>
              <StatusDot status={s}/>
              <span className="truncate text-fg-muted">{STATUS_LABEL_KO[s]}</span>
              <span className="tabular ml-auto font-semibold text-fg">{byStatus[s]}</span>
            </li>))}
        </ul>

        <SectionTitle>공장별 요약</SectionTitle>
        <div className="mx-3 space-y-2">
          {factories.map(({ f, machines, collector, outbox, uploader, running, problem, throughput }) => (<div key={f.id} className="rounded-md border border-border bg-bg p-2 text-[11px]">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-fg">{f.name}</span>
                <span className="tabular text-fg-subtle">{machines.length}대</span>
                <span className="tabular ml-auto text-fg">{formatNumber(throughput, throughput >= 100 ? 0 : 1)} shots/min</span>
              </div>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-fg-muted">
                <span title="정상 + 수집 중 설비">
                  가동 <b className="tabular text-status-healthy">{running}</b>
                </span>
                <span title="경고 + 오류 설비">
                  문제 <b className={cn("tabular", problem > 0 ? "text-status-error" : "text-fg")}>{problem}</b>
                </span>
                <span title="정지 설비">
                  정지 <b className="tabular text-fg">{machines.filter((n) => n.data.status === "offline").length}</b>
                </span>
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {[collector, outbox, uploader].map((n) => n && (<button key={n.id} type="button" onClick={() => requestFocusNode(n.id)} title={`${n.data.label} · ${STATUS_LABEL_KO[n.data.status]}`} className="inline-flex max-w-full items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[10px] text-fg-muted hover:bg-surface-hover hover:text-fg">
                        <StatusDot status={n.data.status} pulse/>
                        <span className="truncate">{n.id.split(":")[0]}</span>
                      </button>))}
              </div>
            </div>))}
        </div>

        <SectionTitle right={<span className="tabular font-normal normal-case">{topProblems.length}</span>}>
          상위 문제 노드
        </SectionTitle>
        {topProblems.length === 0 ? (<div className="px-3 text-[11px] text-status-healthy">문제 노드가 없습니다.</div>) : (<ul className="mx-2 space-y-px">
            {topProblems.map((n) => (<li key={n.id}>
                <button type="button" onClick={() => requestFocusNode(n.id)} title={`${n.data.label} · ${STATUS_LABEL_KO[n.data.status]} · 재시도 ${n.data.retryCount}`} className="flex w-full min-w-0 items-center gap-2 rounded px-1.5 py-1 text-left text-xs text-fg-muted hover:bg-surface-hover hover:text-fg">
                  <StatusDot status={n.data.status} pulse/>
                  <NodeTypeIcon nodeType={n.data.nodeType} size={12}/>
                  <span className="min-w-0 flex-1 truncate">{n.data.label}</span>
                  <span className="shrink-0 text-[10px]" style={{ color: statusColorVar(n.data.status) }}>
                    {STATUS_LABEL_KO[n.data.status]}
                  </span>
                </button>
              </li>))}
          </ul>)}
      </div>
    </div>);
}
export const NodeDetailPanel = memo(function NodeDetailPanel({ onClose, className }) {
    const selectedId = usePipelineStore(selectSelectedNodeId);
    const node = usePipelineStore(selectSelectedNode);
    return (<aside className={cn("flex h-full min-h-0 min-w-0 flex-col bg-bg-elevated", className)} aria-label="노드 상세">
      {node ? (<NodeDetail key={node.id} node={node} onClose={onClose}/>) : selectedId ? (<div className="p-3 text-xs text-fg-subtle">
          선택한 노드({selectedId})를 찾을 수 없습니다.
          <button type="button" onClick={onClose} className="ml-2 text-accent hover:underline">
            선택 해제
          </button>
        </div>) : (<PipelineSummaryView />)}
    </aside>);
});
